import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Email, EmailActivity, htmlToPlainText } from '@vsolv/common/email/domain';
import { ToastService } from '@vsolv/vectors-ui/alert';
import { catchError, EMPTY, firstValueFrom, map, shareReplay, switchMap } from 'rxjs';
import { emailStatus } from '../emails/emails.page';

@Component({
  templateUrl: './email.page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EmailPage {
  private router = inject(Router);
  private http = inject(HttpClient);
  private route = inject(ActivatedRoute);
  private toastSvc = inject(ToastService);
  private changeDetector = inject(ChangeDetectorRef);

  sending = false;
  previewFormat: 'html' | 'text' = 'html';

  readonly emailId$ = this.route.paramMap.pipe(map(params => params.get('emailId')));

  readonly email$ = this.emailId$.pipe(
    switchMap(emailId => {
      if (!emailId) {
        return this.handleError();
      }

      return this.http.get<Email & { activity: EmailActivity[] }>(`/api/emails/${emailId}`);
    }),
    map(email => ({ ...email, plainText: htmlToPlainText(email.html) })),
    catchError(() => this.handleError()),
    shareReplay(1)
  );

  readonly activity$ = this.email$.pipe(
    map(email => {
      return email.activity.map(activity => {
        return {
          ...activity,
          status: emailStatus([activity]),
          message: pargeMessage(activity.message),
        };
      });
    })
  );

  private handleError() {
    this.router.navigate(['..'], { relativeTo: this.route });
    return EMPTY;
  }

  setPreviewFormat(value: unknown) {
    if (value === 'html' || value === 'text') {
      this.previewFormat = value;
      this.changeDetector.markForCheck();
    }
  }

  async resend() {
    this.sending = true;
    this.changeDetector.markForCheck();

    try {
      const emailId = await firstValueFrom(this.emailId$);
      await firstValueFrom(this.http.post(`/api/emails/${emailId}/resend`, {}));
      this.toastSvc.show({ type: 'success', title: 'Email resent', text: 'The email has been resent' });
    } catch (err) {
      console.error(err);
      this.toastSvc.show({
        type: 'error',
        title: 'Something went wrong',
        text: 'Unable to resend email (see console for details)',
      });

      // Delay re-enabling the button by 1sec to ensure no abusing double clicks
      await new Promise(res => setTimeout(res, 1000));
    } finally {
      this.sending = false;
      this.changeDetector.markForCheck();
    }
  }
}

function pargeMessage(message: string | null) {
  try {
    return JSON.parse(message ?? '{}');
  } catch {
    return {};
  }
}
